import {AttrsSchema, Component, jsx} from 'panel';

import {abbreviateNumber, formatPercent} from 'common/util';
import {defineAndMakeJsxFactory} from 'common/util/jsx';
import {SIZE, TextHighlightEl, THEME} from 'common/components/text-highlight';

import css from './index.styl?inline';

export enum MetricValueType {
  Percent = `percent`,
  Number = `number`,
}

export enum MetricChartSize {
  Small = `small`,
  Large = `large`,
}

export interface MetricChartAttributes {
  'comparison-value'?: number;
  label?: string;
  value: number;
  'sub-label'?: string;
  'value-type'?: MetricValueType;
  size?: MetricChartSize;
  color?: string;
}

export class MetricChart extends Component<EmptyObj, MetricChartAttributes> {
  static get attrsSchema(): AttrsSchema<MetricChartAttributes> {
    return {
      ...super.attrsSchema,
      'comparison-value': `number`,
      label: `string`,
      value: `number`,
      'sub-label': `string`,
      'value-type': {type: `string`, enum: Object.values(MetricValueType), default: MetricValueType.Number},
      size: {type: `string`, enum: Object.values(MetricChartSize), default: MetricChartSize.Large},
      color: `string`,
    };
  }

  get config() {
    return {
      useShadowDom: true,
      css,
      template: () => this.renderTemplate(),
    };
  }

  renderTemplate() {
    const value = this.attr(`value`);

    // TODO: sigfigs/precision based on width?
    const displayedValue =
      this.attr(`value-type`) === MetricValueType.Percent ? formatPercent(value) : abbreviateNumber(value);

    return (
      <div sel='.root' class={{[`size-${this.attr(`size`)}`]: true}}>
        <div sel='.segment-name'>
          {this.attr(`color`) && <div sel='.segment-color' style={{'background-color': this.attr(`color`)}} />}
          <div sel='.segment-label'>{this.attr(`label`)}</div>
        </div>

        <div sel='.value'>{displayedValue}</div>

        {this.attr(`sub-label`) && <div sel='.sub-label'>{this.attr(`sub-label`)}</div>}
        {this.attr(`comparison-value`) && this.renderComparison()}
      </div>
    );
  }

  renderComparison() {
    const comparisonValue = this.attr(`comparison-value`);
    const percentChange = (this.attr(`value`) - comparisonValue) / comparisonValue;
    const percentChangePositive = percentChange > 0;

    return (
      <div sel='.change-row'>
        <TextHighlightEl
          attrs={{
            text: formatPercent(Math.abs(percentChange)),
            icon: percentChangePositive ? `arrow-up` : `arrow-down`,
            theme: percentChangePositive ? THEME.POSITIVE : THEME.NEGATIVE,
            size: SIZE.SMALL,
          }}
        />
      </div>
    );
  }
}

interface MetricChartJsxProps {
  attrs: MetricChartAttributes;
}

export const MetricChartEl = defineAndMakeJsxFactory<MetricChartJsxProps>(`mp-metric`, MetricChart);
